import { Link } from 'react-router-dom';
import type { SunStatus } from '@kaeliux/shared';
import { SUN_CONTROL_CLAIM_POPULATION, SUN_CONTROL_LEDGER_CAP, SUN_CONTROL_MAINTAIN_POPULATION } from '@kaeliux/shared';
import { formatCountdown, formatFullNumber } from '../utils/formatters';
import { Badge } from './ui/Badge';
import { linkButtonClasses } from './ui/buttonStyles';

function until(date: Date | undefined, now: number): string {
    return formatCountdown(Math.max(0, (date?.getTime() ?? now) - now));
}

interface SunDetailsProps {
    galaxy: number;
    system: number;
    sun: SunStatus | undefined;
    viewerUserId: string | undefined;
    /** The viewer leads an alliance with a Team planet to dispatch from (issue #158). */
    canSunAttack: boolean;
    /** `useMyAlliance`/`useTeamPlanet` still loading — show a pending affordance instead of nothing. */
    canSunAttackPending: boolean;
    now: number;
}

/** Who holds the Sun, as this viewer may see it — named, concealed, gone, or nobody. */
function holderText(sun: SunStatus): string | null {
    const control = sun.control;
    if (!control) return null;
    const who = control.alliance
        ? `[${control.alliance.tag}] ${control.alliance.name}`
        : control.concealed ? 'a concealed alliance' : 'an alliance that no longer exists';
    return `${control.status === 'controlled' ? 'Controlled' : 'Claimed'} by ${who}`;
}

/**
 * Sun status for the Galaxy map's details popup (`position === 0`). Every
 * viewer sees whether it's under attack or on cooldown and who holds it —
 * unless the holder's Intelligence conceals it. An uncontrolled Sun shows
 * its Sun Support defenders (the full roster to the "commander of sun
 * defence" only); a held Sun shows its public timers, and its garrison to
 * the holding alliance's own members. The actions follow the two fights:
 * Sun Attack and Sun Support at an uncontrolled Sun, a reclaim at another
 * alliance's, a garrison at your own.
 */
export default function SunDetails({ galaxy, system, sun, viewerUserId, canSunAttack, canSunAttackPending, now }: SunDetailsProps) {
    const coords = `${galaxy}:${system}:0`;
    const isCommander = !!sun?.commanderUserId && sun.commanderUserId === viewerUserId;
    const control = sun?.control;
    const ownSun = !!control?.viewerIsController;
    const attackLabel = control ? 'Reclaim' : 'Sun Attack';
    const immune = control?.reclaimImmuneUntil && control.reclaimImmuneUntil.getTime() > now;

    return (
        <div className="flex flex-col gap-2 pr-6" data-testid="sun-details-panel">
            <h3 className="text-title font-semibold text-content-primary">Sun</h3>
            <p className="text-content-muted text-label">Coordinates <span className="font-mono text-content-secondary">{coords}</span></p>
            {sun && (sun.underAttack || sun.onCooldown) ? (
                <p className="text-body font-semibold" data-testid="sun-status-text">
                    {sun.underAttack
                        ? <span className="text-status-danger">Under attack</span>
                        : <span className="text-status-warning">On cooldown</span>}
                </p>
            ) : (
                <p className="text-body text-status-success font-semibold" data-testid="sun-status-text">Calm</p>
            )}
            {sun?.onCooldown && sun.nextAttackEligibleAt && (
                <p className="text-content-muted text-label" data-testid="sun-cooldown-text">
                    No attack may be sent for {until(sun.nextAttackEligibleAt, now)}.
                </p>
            )}

            {sun && control ? (
                <div className="flex flex-col gap-1 border-t border-hairline pt-2" data-testid="sun-control">
                    <div className="flex flex-wrap items-center gap-2">
                        <p className="text-body text-content-primary font-semibold" data-testid="sun-control-text">{holderText(sun)}</p>
                        {control.stronghold && <Badge tone="accent" data-testid="sun-stronghold-badge">Stronghold</Badge>}
                    </div>
                    {control.stronghold ? (
                        <p className="text-content-muted text-label">It held off a reclaim and can never be attacked again.</p>
                    ) : control.status === 'claimed' ? (
                        <p className="text-content-muted text-label" data-testid="sun-claim-text">
                            The claim lapses in {until(control.claimExpiresAt, now)} unless {formatFullNumber(SUN_CONTROL_CLAIM_POPULATION)} population of garrison has landed.
                        </p>
                    ) : immune ? (
                        <p className="text-content-muted text-label" data-testid="sun-immunity-text">
                            Its garrison landed recently — no reclaim may be sent for {until(control.reclaimImmuneUntil, now)}.
                        </p>
                    ) : null}
                    {ownSun && control.garrisonPopulation !== undefined && (
                        <p className="text-content-secondary text-label" data-testid="sun-garrison-text">
                            Your garrison: {formatFullNumber(control.garrisonPopulation)} / {formatFullNumber(SUN_CONTROL_LEDGER_CAP)} population
                        </p>
                    )}
                    {ownSun && control.topUpExpiresAt && (
                        <p className="text-status-danger text-label">
                            Restore it to {formatFullNumber(SUN_CONTROL_MAINTAIN_POPULATION)} population within {until(control.topUpExpiresAt, now)}, or the Sun is lost.
                        </p>
                    )}
                    {control.concealed && !ownSun && (
                        <p className="text-content-muted text-label">Send Espionage to learn who holds it.</p>
                    )}
                </div>
            ) : (
                <p className="text-content-muted text-label" data-testid="sun-defender-summary">
                    {sun?.defenderCount ?? 0} defending fleet{(sun?.defenderCount ?? 0) === 1 ? '' : 's'} ·{' '}
                    {(sun?.totalDefenderPopulation ?? 0).toLocaleString()} population
                </p>
            )}

            {isCommander && sun?.defenders && sun.defenders.length > 0 && (
                <div className="flex flex-col gap-1 mt-1 border-t border-hairline pt-2" data-testid="sun-commander-roster">
                    <p className="text-status-info text-label font-semibold">Commander of Sun Defence — full roster</p>
                    {sun.defenders.map((d) => (
                        <p key={d.fleetId} className="text-content-muted text-label">
                            {d.userId === viewerUserId ? 'You' : d.userId} — {d.race}
                        </p>
                    ))}
                </div>
            )}
            <div className="flex flex-wrap gap-2 mt-1">
                {ownSun ? (
                    canSunAttack ? (
                        <Link to={`/team-planet/fleets?mission=sunGarrison&target=${coords}`} className={linkButtonClasses('secondary', 'sm')}>
                            Send garrison
                        </Link>
                    ) : null
                ) : canSunAttack && !control?.stronghold ? (
                    <Link to={`/team-planet/fleets?target=${coords}`} className={linkButtonClasses('secondary', 'sm')}>{attackLabel}</Link>
                ) : canSunAttackPending ? (
                    <span
                        className={`${linkButtonClasses('secondary', 'sm')} opacity-40 animate-pulse`}
                        aria-hidden="true"
                        data-testid="sun-attack-link-pending"
                    >
                        {attackLabel}
                    </span>
                ) : null}
                {!control && (
                    <Link to={`/fleets?mission=sunDefence&target=${coords}`} className={linkButtonClasses('secondary', 'sm')}>Sun Support</Link>
                )}
                {control?.concealed && !ownSun && (
                    <Link to={`/fleets?mission=espionage&target=${coords}`} className={linkButtonClasses('secondary', 'sm')}>Espionage</Link>
                )}
            </div>
        </div>
    );
}
